import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Drawer, Form, Input, message, Modal, Popconfirm, Space, Table, Tag, Typography } from 'antd';
import { PlusOutlined, SearchOutlined, TeamOutlined } from '@ant-design/icons';
import * as api from '../../models/permissionGroupAPI';
import PermissionEditor from '../../permissions/PermissionEditor';
import { modules } from '../../permissions/catalog';
import { permissionDetails } from '../../permissions/policy';

const emptyGroup = () => ({ Name: '', IsDevisManager: false, PermissionSettings: permissionDetails({ Permissions: [] }, true) });

export default function PermissionGroupManagement() {
  const [search, setSearch] = useState('');
  const [dirty, setDirty] = useState(false);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const refresh = useCallback(async () => {
    setLoading(true);
    try { setGroups((await api.listPermissionGroups()).data); setError(null); }
    catch (e) { setError(e.response?.data?.message || 'Impossible de charger les groupes.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => {
    refresh();
    window.addEventListener('permission-memberships-changed', refresh);
    return () => window.removeEventListener('permission-memberships-changed', refresh);
  }, [refresh]);
  const edit = group => {
    setDirty(false);
    setEditing(group || null);
    setOpen(true);
  };
  useEffect(() => {
    if (open) { form.resetFields(); form.setFieldsValue(editing || emptyGroup()); }
  }, [open, editing, form]);
  const close = () => {
    if (saving) return;
    if (!dirty) { setOpen(false); return; }
    Modal.confirm({ title: 'Quitter sans enregistrer ?', content: 'Les modifications de ce groupe seront perdues.', okText: 'Quitter', cancelText: 'Continuer à modifier', onOk: () => setOpen(false) });
  };
  const save = async () => {
    try {
      await form.validateFields();
      const { Name, IsDevisManager, PermissionSettings } = form.getFieldsValue(true);
      const values = { Name, IsDevisManager, PermissionSettings };
      setSaving(true);
      if (editing) await api.updatePermissionGroup(editing.id, values);
      else await api.createPermissionGroup(values);
      message.success(editing ? 'Groupe mis à jour pour tous ses membres.' : 'Groupe créé.');
      window.dispatchEvent(new Event('permissions-refresh'));
      window.dispatchEvent(new Event('permission-groups-changed'));
      setOpen(false);
      await refresh();
    } catch (e) {
      if (!e.errorFields) message.error(e.response?.data?.message || 'Impossible d’enregistrer le groupe.');
    } finally { setSaving(false); }
  };
  const remove = async group => {
    setDeletingId(group.id);
    try {
      await api.deletePermissionGroup(group.id);
      message.success('Groupe supprimé. Les droits directs des utilisateurs sont conservés.');
      window.dispatchEvent(new Event('permissions-refresh'));
      window.dispatchEvent(new Event('permission-groups-changed'));
      await refresh();
    } catch (e) { message.error(e.response?.data?.message || 'Impossible de supprimer le groupe.'); }
    finally { setDeletingId(null); }
  };
  return <Space direction="vertical" style={{ width: '100%' }} size="middle">
    <div className="permission-overview"><div><TeamOutlined /> <strong>{groups.length}</strong> groupes</div><span className="permission-caption">Créer un groupe → choisir ses droits → l’ajouter aux utilisateurs</span></div>
    {error && <Alert type="error" message={error} action={<Button onClick={refresh}>Réessayer</Button>} />}
    <div className="permission-group-toolbar"><Input prefix={<SearchOutlined />} allowClear aria-label="Rechercher un groupe" placeholder="Rechercher un groupe…" value={search} onChange={e => setSearch(e.target.value)} /><Button type="primary" icon={<PlusOutlined />} onClick={() => edit(null)}>Créer un groupe</Button></div>
    <Table rowKey="id" dataSource={groups.filter(group => group.Name.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()))} loading={loading} locale={{ emptyText: search ? 'Aucun groupe trouvé' : 'Créez votre premier groupe de permissions' }} scroll={{ x: 650 }} columns={[
      { title: 'Nom du groupe', dataIndex: 'Name', render: name => <strong>{name}</strong> },
      { title: 'Accès inclus', key: 'access', render: (_, group) => {
        const settings = permissionDetails(group, true);
        const included = modules.filter(m => settings.modules[m.id].full || Object.values(settings.modules[m.id].actions).some(Boolean));
        return <Space size={[0, 4]} wrap>{included.slice(0, 3).map(m => <Tag key={m.id}>{m.label}</Tag>)}{included.length > 3 && <Tag>+{included.length - 3}</Tag>}{!included.length && <Typography.Text type="secondary">Aucun module</Typography.Text>}</Space>;
      } },
      { title: 'Membres', dataIndex: 'MemberCount', width: 110, render: count => <Tag icon={<TeamOutlined />}>{count || 0}</Tag> },
      { title: 'Actions', key: 'actions', render: (_, group) => <Space>
        <Button onClick={() => edit(group)}>Modifier</Button>
        <Popconfirm title={`Supprimer le groupe « ${group.Name} » ?`}
          description={`${group.MemberCount || 0} utilisateur(s) perdront les droits hérités de ce groupe. Leurs autres droits seront conservés.`}
          onConfirm={() => remove(group)} okText="Supprimer" cancelText="Annuler" okButtonProps={{ danger: true }}>
          <Button danger loading={deletingId === group.id}>Supprimer</Button>
        </Popconfirm>
      </Space> },
    ]} />
    <Drawer title={editing ? `Modifier le groupe : ${editing.Name}` : 'Créer un groupe de permissions'} width="min(100vw, 1040px)"
      open={open} onClose={close} forceRender
      footer={<div className="permission-drawer-footer"><span className="permission-caption">{editing ? `${editing.MemberCount || 0} membre(s) concernés` : 'Les utilisateurs seront affectés depuis leur fiche.'}{dirty ? ' · Modifications non enregistrées' : ''}</span><Space><Button disabled={saving} onClick={close}>Annuler</Button><Button type="primary" loading={saving} onClick={save}>Enregistrer</Button></Space></div>}>
      <Form form={form} layout="vertical" initialValues={emptyGroup()} onValuesChange={() => setDirty(true)}>
        {editing?.MemberCount > 0 && <Alert style={{ marginBottom: 16 }} type="info" showIcon message={`Ces droits sont partagés par ${editing.MemberCount} membre(s).`} />}
        <Form.Item name="Name" label="Nom du groupe" rules={[{ required: true, whitespace: true, message: 'Saisissez un nom de groupe.' }, { max: 100 }]}>
          <Input maxLength={100} placeholder="Ex. Équipe ventes" disabled={saving} />
        </Form.Item>
        <PermissionEditor groupMode disabled={saving} onPermissionsChange={() => setDirty(true)} />
      </Form>
    </Drawer>
  </Space>;
}
